import { useState, useRef, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card, CardContent, IconButton, Chip } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import TwitterIcon from '@mui/icons-material/Twitter'
import LanguageIcon from '@mui/icons-material/Language'
import SEO from '../utils/seo'
import Tag from '../components/Tag'
import Callout from '../components/Callout'
import ShareButtons from '../components/ShareButtons'
import Loading from '../components/Loading'
import { formatDate, formatEpisodeNumber } from '../utils/format'
import { trackEpisodePlay, trackEpisodePause } from '../utils/analytics'
import { colors } from '../styles/theme'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const EpisodeDetail = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const audioRef = useRef(null)
  const [episode, setEpisode] = useState(null)
  const [allEpisodes, setAllEpisodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [epRes, allRes] = await Promise.all([
          fetch(`${API_URL}/episodes/${slug}`),
          fetch(`${API_URL}/episodes`)
        ])

        if (!epRes.ok) {
          navigate('/404', { replace: true })
          return
        }

        const epData = await epRes.json()
        const allData = await allRes.json()
        setEpisode(epData)
        setAllEpisodes(allData)
      } catch (error) {
        console.error('Error fetching episode:', error)
        navigate('/404', { replace: true })
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [slug, navigate])

  if (loading) return <Loading />
  if (!episode) return null

  // Find prev/next episodes
  const currentIndex = allEpisodes.findIndex((ep) => ep.slug === slug)
  const prevEpisode = currentIndex < allEpisodes.length - 1 ? allEpisodes[currentIndex + 1] : null
  const nextEpisode = currentIndex > 0 ? allEpisodes[currentIndex - 1] : null

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
        trackEpisodePause(episode.slug, currentTime)
      } else {
        audioRef.current.play()
        trackEpisodePlay(episode.slug, episode.title)
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration)
    }
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getYouTubeVideoId = (url) => {
    if (!url) return null
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/)
    return match ? match[1] : null
  }

  const videoId = getYouTubeVideoId(episode.videoUrl)

  const renderShowNote = (note, index) => {
    switch (note.type) {
      case 'h2':
        return (
          <h2 key={index} className="text-2xl font-bold text-off-white mt-8 mb-4">
            {note.text}
          </h2>
        )
      case 'p':
        return (
          <p key={index} className="text-gray-300 mb-4 leading-relaxed">
            {note.text}
          </p>
        )
      case 'link':
        return (
          <a
            key={index}
            href={note.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan hover:text-cyan/80 gradient-underline block mb-2"
          >
            {note.text} →
          </a>
        )
      case 'code':
        return (
          <pre key={index} className="code-block mb-4 custom-scrollbar">
            <code>{note.text}</code>
          </pre>
        )
      default:
        return null
    }
  }

  const currentUrl = `https://promptedthoughts.dev/episodes/${episode.slug}`

  return (
    <>
      <SEO
        title={episode.title}
        description={episode.summary}
        image={episode.coverImage}
        url={currentUrl}
        podcast={{
          title: episode.title,
          summary: episode.summary,
          date: episode.date,
          duration: episode.duration,
          audioUrl: episode.audioUrl,
        }}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="section"
      >
        <div className="container-custom max-w-4xl">
          {/* Back button */}
          <Link
            to="/episodes"
            className="inline-flex items-center text-cyan hover:text-cyan/80 mb-8 gradient-underline"
          >
            <ArrowBackIcon sx={{ mr: 1 }} /> Back to Episodes
          </Link>

          {/* Episode Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <Chip
                label={formatEpisodeNumber(episode.number)}
                sx={{
                  backgroundColor: colors.info,
                  color: colors.textOnAccent,
                  fontFamily: 'JetBrains Mono, monospace',
                  fontWeight: 600,
                }}
              />
              <span className="text-gray-400">{formatDate(episode.date)}</span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-400 font-mono">{episode.duration}</span>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {episode.tags.map((tag) => (
                <Tag key={tag} tag={tag} />
              ))}
            </div>

            <ShareButtons url={currentUrl} title={episode.title} episodeSlug={episode.slug} />
          </motion.div>

          {/* Video Player */}
          {videoId && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mb-12"
            >
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  src={`https://www.youtube.com/embed/${videoId}`}
                  title={episode.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </motion.div>
          )}

          {/* Audio Player */}
          {!videoId && episode.audioUrl && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mb-12"
            >
            <Card
              sx={{
                backgroundColor: colors.bgCard,
                borderRadius: '12px',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <div className="flex items-center gap-4">
                  <IconButton
                    onClick={togglePlay}
                    sx={{
                      backgroundColor: colors.info,
                      color: colors.textOnAccent,
                      width: 64,
                      height: 64,
                      '&:hover': {
                        backgroundColor: colors.infoHover,
                      },
                    }}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? <PauseIcon sx={{ fontSize: 32 }} /> : <PlayArrowIcon sx={{ fontSize: 32 }} />}
                  </IconButton>

                  <div className="flex-grow">
                    <div className="text-off-white font-medium mb-2">
                      {isPlaying ? 'Now Playing' : 'Ready to Play'}
                    </div>
                    <div className="w-full bg-slate rounded-full h-2 mb-2">
                      <div
                        className="bg-cyan h-2 rounded-full transition-all"
                        style={{ width: `${(currentTime / duration) * 100}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 font-mono">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>
                </div>

                <audio
                  ref={audioRef}
                  src={episode.audioUrl}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={() => setIsPlaying(false)}
                />
              </CardContent>
            </Card>
          </motion.div>
          )}

          {/* The Prompts - HERO SECTION */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mb-16"
          >
            {/* System Context */}
            <div className="mb-8 p-6 bg-gradient-to-br from-slate/40 to-slate/20 rounded-xl border border-cyan/20">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs font-mono text-cyan uppercase tracking-widest font-bold">
                  System Context
                </span>
                <div className="h-px flex-grow bg-gradient-to-r from-cyan/40 to-transparent"></div>
              </div>
              <p className="text-gray-300 leading-relaxed italic">
                "Every episode starts with a single, focused question. We go deep—exploring design, architecture, and the practical realities of shipping AI-native software. No hype, just engineering."
              </p>
            </div>

            {/* Episode Prompt - The Star */}
            <div className="relative p-8 bg-gradient-to-br from-purple/10 via-slate/30 to-cyan/10 rounded-2xl border-2 border-purple/30 shadow-2xl">
              <div className="absolute top-4 left-4">
                <span className="text-xs font-mono text-purple uppercase tracking-widest font-bold">
                  Episode Prompt
                </span>
              </div>
              <blockquote className="mt-8 text-2xl md:text-3xl font-semibold text-off-white leading-relaxed">
                <span className="text-purple/60 text-4xl">"</span>
                {episode.prompt}
                <span className="text-purple/60 text-4xl">"</span>
              </blockquote>
              <div className="absolute -bottom-2 -right-2 w-20 h-20 bg-gradient-to-br from-purple/20 to-cyan/20 rounded-full blur-xl"></div>
              <div className="absolute -top-2 -left-2 w-16 h-16 bg-gradient-to-br from-cyan/20 to-purple/20 rounded-full blur-xl"></div>
            </div>
          </motion.div>

          {/* Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mb-12"
          >
            <p className="text-xl text-gray-300 leading-relaxed">{episode.summary}</p>
          </motion.div>

          {/* Show Notes */}
          {episode.showNotes && episode.showNotes.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-off-white mb-6">Show Notes</h2>
              <div className="prose prose-invert max-w-none">
                {episode.showNotes.map((note, index) => renderShowNote(note, index))}
              </div>
            </motion.div>
          )}

          {/* Guest Section */}
          {episode.guest && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-off-white mb-6">Featured Guest</h2>
              <Card
                sx={{
                  backgroundColor: colors.bgCard,
                  borderRadius: '12px',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <div className="flex items-start gap-6">
                    <img
                      src={episode.guest.photo || '/images/placeholder-guest.jpg'}
                      alt={episode.guest.name}
                      className="w-24 h-24 rounded-full object-cover"
                    />
                    <div className="flex-grow">
                      <h3 className="text-2xl font-bold text-off-white mb-1">
                        {episode.guest.name}
                      </h3>
                      <p className="text-cyan mb-2">
                        {episode.guest.role} at {episode.guest.company}
                      </p>
                      <div className="flex gap-2">
                        {episode.guest.links?.map((link) => (
                          <a
                            key={link.label}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-cyan transition-colors"
                            aria-label={link.label}
                          >
                            {link.label === 'LinkedIn' && <LinkedInIcon />}
                            {link.label === 'Twitter' && <TwitterIcon />}
                            {(link.label === 'Website' || link.label === 'Blog') && <LanguageIcon />}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Prev/Next Navigation */}
          {allEpisodes.length > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="flex justify-between items-center pt-8 border-t border-slate"
            >
              {prevEpisode ? (
                <Link
                  to={`/episodes/${prevEpisode.slug}`}
                  className="flex items-center text-cyan hover:text-cyan/80 gradient-underline"
                >
                  <ArrowBackIcon sx={{ mr: 1 }} />
                  <span>Previous Episode</span>
                </Link>
              ) : (
                <div />
              )}

              {nextEpisode ? (
                <Link
                  to={`/episodes/${nextEpisode.slug}`}
                  className="flex items-center text-cyan hover:text-cyan/80 gradient-underline"
                >
                  <span>Next Episode</span>
                  <ArrowForwardIcon sx={{ ml: 1 }} />
                </Link>
              ) : (
                <div />
              )}
            </motion.div>
          )}
        </div>
      </motion.div>
    </>
  )
}

export default EpisodeDetail
